import TableTemplate, {
  IExtraTypeUIProps,
  IFilterTableCols,
} from "../../TableTemplate/tableTemplate";
import {
  IChartCommonSearchParam,
  IPairPositionReport,
  IPairPositionSearchParams,
} from "../../../Interfaces/optionDomain";
import { CommonTableUI } from "../../TableTemplate/common";
import { ExtraType, Report } from "../../../Interfaces/TableInterfaces";
import {
  getOptionDomainChart,
  getPairPositionTable,
} from "../../../Requests/optionDomain";
import { Additional } from "./Additional/Additional";
import { useCallback } from "react";
import CellWithChart from "../../TableTemplate/CellWithChart";
import ChartSearchParamUI from "./ChartSearchParamUI";
import { useScroll } from "../../../Hooks/useScroll";
import { usePairWatchListSnapshot } from "../../../Hooks/usePairWatchListSnapshot";

interface IProps {
  filterCols: IFilterTableCols;
  searchParams: IPairPositionSearchParams;
  refreshFlag: boolean;
  onRowClick: (reports: IPairPositionReport) => void;
  selectedTableRow: number | null;
}

const PairPositionTable = ({
  filterCols,
  searchParams,
  refreshFlag,
  onRowClick,
  selectedTableRow,
}: IProps) => {
  const { ref } = useScroll<HTMLDivElement>("pairPositionTableScroll");

  const { snapshotPending, retryKey, handleSnapshotError, handleSnapshotResponse } =
    usePairWatchListSnapshot();

  const handleRowClick = (report: IPairPositionReport) => {
    onRowClick(report);
  };

  const getExtraTypeUI = useCallback(
    ({
      headerName,
      extraType,
      row,
      cellType,
      translateHeaderName,
    }: IExtraTypeUIProps<IPairPositionReport>) => {
      switch (extraType) {
        case ExtraType.WithChart:
          return (
            <CellWithChart<IPairPositionReport, IChartCommonSearchParam>
              SearchParamUI={({ changeHandler, onSubmit, searchParams }) => (
                <ChartSearchParamUI
                  searchParams={searchParams}
                  onSubmit={onSubmit}
                  onChange={changeHandler}
                />
              )}
              cellType="Price"
              headerName={headerName}
              translateHeaderName={translateHeaderName}
              title={[translateHeaderName || headerName, row.instrumentName1]
                .filter(Boolean)
                .join(" — ")}
              row={row}
              extraType={extraType}
              chartGetter={(searchParams) =>
                getOptionDomainChart(
                  headerName,
                  row.instrumentId1,
                  row.instrumentName1,
                  row.instrumentId2,
                  row.instrumentName2,
                  row.positionType1,
                  searchParams.fromDate,
                  searchParams.toDate,
                )
              }
              withDate
            />
          );

        default:
          return <></>;
      }
    },
    [searchParams],
  );

  return (
    <div className="relative w-[calc(100%-335px)]">
      {snapshotPending && (
        <div className="py-6 text-center text-sm text-gray-600">
          اطلاعات جفت پوزیشن در حال آماده‌سازی است…
        </div>
      )}
      <TableTemplate<IPairPositionSearchParams, IPairPositionReport>
        TableUI={({
          reports,
          onAction,
          sortingType,
          setSortingType,
          scrollKey,
        }) => (
          <CommonTableUI
            reports={reports}
            onAction={onAction}
            tableBoxClassName="!max-h-[600px] !max-w-full"
            refreshFlag={refreshFlag}
            AdditionalBody={Additional}
            sortingType={sortingType}
            setSortingType={setSortingType}
            onTableRowClick={handleRowClick} // Use the modified click handler
            activeRowId={selectedTableRow || undefined}
            getExtraTypeUI={getExtraTypeUI}
            ref={ref}
            scrollKey={scrollKey}
            filterCols={filterCols}
          />
        )}
        refreshTableTime={5000}
        getDataTable={getPairPositionTable}
        searchParameters={searchParams}
        withSort
        tableWrapperClassname="w-full"
        hideUntilResponse
        silentRefreshKey={retryKey}
        onFetchError={handleSnapshotError}
        onResponse={handleSnapshotResponse}
      />
    </div>
  );
};

export default PairPositionTable;
